import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import { Route as RouteIcon, ExternalLink, Printer, X } from "lucide-react";
import { api, STATUS, OPEN_STATUSES } from "@/lib/api";
import { planRoute, googleMapsUrl } from "@/lib/route";
import { PageTitle, StatusBadge } from "@/components/StatusBadge";

const COLORS = { nieuw: "#F59E0B", inspecteren: "#38BDF8", bezoeken: "#E879F9", doorgezet: "#F43F5E" };
const BRT = "https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png";
const LUCHT = "https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg";

const useLeaflet = (el) => {
  const map = useRef(null);
  const layer = useRef(null);
  useEffect(() => {
    const m = L.map(el.current, { maxZoom: 19 }).setView([52.1, 5.2], 8);
    const brt = L.tileLayer(BRT, { maxZoom: 19, attribution: "PDOK BRT" }).addTo(m);
    const lf = L.tileLayer(LUCHT, { maxZoom: 19, attribution: "PDOK Luchtfoto" });
    L.control.layers({ Kaart: brt, Luchtfoto: lf }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    map.current = m;
    return () => m.remove();
  }, [el]);
  return { map, layer };
};

export default function Kaart() {
  const el = useRef(null);
  const { map, layer } = useLeaflet(el);
  const [statuses, setStatuses] = useState(OPEN_STATUSES);
  const [sel, setSel] = useState([]);
  const { data = [] } = useQuery({
    queryKey: ["kaart", statuses],
    queryFn: () => api.get("/kaart/meldingen", { params: { statuses: statuses.join(",") } }).then((r) => r.data),
  });
  const plan = useMemo(() => {
    const pts = data.filter((m) => sel.includes(m.id));
    return pts.length >= 2 ? planRoute(pts[0], pts.slice(1)) : null;
  }, [data, sel]);

  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  useEffect(() => {
    if (!layer.current) return;
    layer.current.clearLayers();
    data.forEach((m) => {
      const picked = sel.includes(m.id);
      const mk = L.circleMarker([m.lat, m.lon], { radius: picked ? 9 : 7, color: picked ? "#fff" : "#0b1220", weight: 2, fillColor: COLORS[m.status] || "#94A3B8", fillOpacity: 0.95 });
      mk.bindTooltip(`${m.weergavenaam} · ${STATUS[m.status]?.label}`);
      mk.on("click", () => toggleSel(m.id));
      layer.current.addLayer(mk);
    });
    if (plan) {
      layer.current.addLayer(L.polyline(plan.route.map((p) => [p.lat, p.lon]), { color: "#2563EB", weight: 3, dashArray: "6 4" }));
      plan.route.forEach((p, i) => layer.current.addLayer(L.marker([p.lat, p.lon], { icon: L.divIcon({ className: "", html: `<div style="transform:translate(10px,-22px);background:#2563EB;color:#fff;font:600 11px JetBrains Mono;padding:1px 5px;border-radius:3px">${i + 1}</div>` }) })));
    }
    if (data.length && !sel.length) map.current.fitBounds(L.latLngBounds(data.map((m) => [m.lat, m.lon])), { padding: [40, 40], maxZoom: 17 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sel, plan]);

  return (
    <div className="max-w-7xl mx-auto">
      <PageTitle eyebrow="Veldwerk" title="Meldingenkaart & bezoekroute">
        {OPEN_STATUSES.map((s) => (
          <button key={s} data-testid={`map-filter-${s}`} onClick={() => setStatuses((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x, s]))}
            className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${statuses.includes(s) ? "border-slate-600 text-white" : "border-slate-800 text-slate-500"}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: COLORS[s] }} />{STATUS[s].label}
          </button>
        ))}
      </PageTitle>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="print:hidden lg:col-span-8 h-[620px] relative z-0 isolate rounded-lg overflow-hidden border border-slate-800">
          <div ref={el} className="h-full w-full" data-testid="meldingen-map" />
        </div>
        <aside className="lg:col-span-4 space-y-4 min-w-0">
          <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5" data-testid="route-panel">
            <h3 className="font-display font-semibold flex items-center gap-2 mb-2"><RouteIcon className="h-4 w-4" /> Bezoekroute</h3>
            {!plan && <p className="text-xs text-slate-500">Klik op 2 of meer meldingen (kaart of lijst). De eerste selectie is het startpunt.</p>}
            {plan && (
              <>
                <div className="font-mono text-xs text-slate-400 mb-3" data-testid="route-distance">{plan.route.length} stops · ca. {plan.km.toFixed(1)} km hemelsbreed</div>
                <ol className="space-y-1.5 mb-4">
                  {plan.route.map((p, i) => (
                    <li key={p.id} className="flex items-center gap-2 text-sm" data-testid={`route-stop-${i}`}>
                      <span className="font-mono text-[10px] bg-blue-600 rounded px-1.5">{i + 1}</span>
                      <Link to={`/meldingen/${p.id}`} className="truncate hover:text-blue-300">{p.weergavenaam}</Link>
                    </li>
                  ))}
                </ol>
                <div className="flex gap-2">
                  <a href={googleMapsUrl(plan.route)} target="_blank" rel="noreferrer" data-testid="route-gmaps-link" className="flex-1 h-9 rounded-md bg-blue-600 hover:bg-blue-500 text-xs flex items-center justify-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> Navigeer</a>
                  <button onClick={() => window.print()} data-testid="route-print-btn" className="h-9 px-3 rounded-md border border-slate-700 text-xs flex items-center gap-1.5"><Printer className="h-3.5 w-3.5" /> Print</button>
                  <button onClick={() => setSel([])} data-testid="route-clear-btn" className="h-9 px-3 rounded-md border border-slate-700 text-xs"><X className="h-3.5 w-3.5" /></button>
                </div>
                {plan.route.length > 11 && <p className="text-[11px] text-amber-400 mt-2">Google Maps toont max. 11 stops; de rest staat in de lijst.</p>}
              </>
            )}
          </div>
          <div className="print:hidden border border-slate-800 bg-[#111827]/80 rounded-lg p-3 max-h-[380px] overflow-y-auto" data-testid="map-melding-list">
            {data.map((m) => (
              <label key={m.id} className="flex items-center gap-2 py-1.5 px-1 text-sm cursor-pointer hover:bg-slate-800/40 rounded">
                <input type="checkbox" checked={sel.includes(m.id)} onChange={() => toggleSel(m.id)} data-testid={`map-select-${m.id}`} className="accent-blue-600" />
                <span className="truncate flex-1">{m.weergavenaam}</span><StatusBadge status={m.status} />
              </label>
            ))}
            {data.length === 0 && <p className="text-xs text-slate-500 p-2">Geen meldingen met deze status.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
